import { Container, Graphics } from "pixi.js";
import type { CombatEffectSnapshot } from "@frontier/protocol/contracts";
import { visibleCombatEffects } from "./combat-effect-presentation";

export class AreaEffects {
  readonly container = new Container();
  private readonly graphics = new Graphics();
  private effects: readonly CombatEffectSnapshot[] = [];
  private receivedAt = 0;
  private readonly startedAt = new Map<string, number>();

  constructor() {
    this.container.label = "combat-areas";
    this.container.addChild(this.graphics);
  }

  receive(effects: readonly CombatEffectSnapshot[], now: number): void {
    this.effects = effects;
    this.receivedAt = now;
    const ids = new Set(effects.map((effect) => effect.id));
    for (const id of this.startedAt.keys())
      if (!ids.has(id)) this.startedAt.delete(id);
    for (const effect of effects)
      if (!this.startedAt.has(effect.id)) this.startedAt.set(effect.id, now);
  }

  render(now: number): void {
    const graphics = this.graphics.clear();
    for (const effect of visibleCombatEffects(
      this.effects,
      (now - this.receivedAt) / 1000,
    )) {
      const { x, y, radius, alpha } = effect;
      if (effect.kind === "rail-hit") {
        graphics
          .circle(x, y, radius)
          .stroke({ color: 0xf3b5ff, width: 3, alpha });
        graphics
          .moveTo(x - radius, y - radius)
          .lineTo(x + radius, y + radius)
          .moveTo(x - radius, y + radius)
          .lineTo(x + radius, y - radius)
          .stroke({ color: 0xffffff, width: 1.5, alpha });
      } else if (effect.kind === "repair-field") {
        graphics
          .circle(x, y, radius)
          .fill({ color: 0x69e2b0, alpha: alpha * 0.035 })
          .stroke({ color: 0x69e2b0, width: 1.5, alpha });
        for (let index = 0; index < 4; index++) {
          const angle = (index * Math.PI) / 2 + now / 6000;
          const px = x + Math.cos(angle) * radius * 0.6;
          const py = y + Math.sin(angle) * radius * 0.6;
          graphics
            .moveTo(px - 6, py)
            .lineTo(px + 6, py)
            .moveTo(px, py - 6)
            .lineTo(px, py + 6)
            .stroke({ color: 0xbaffd8, width: 2, alpha });
        }
      } else if (effect.kind === "scan") {
        const angle = now / 500;
        const phase = Math.min(
          1,
          (now - (this.startedAt.get(effect.id) ?? now)) / 800,
        );
        const pulseRadius =
          Math.min(40, radius) + (radius - Math.min(40, radius)) * phase;
        graphics
          .circle(x, y, radius)
          .stroke({ color: 0xffd387, width: 1, alpha: alpha * 0.5 });
        graphics
          .circle(x, y, pulseRadius)
          .stroke({ color: 0xffd387, width: 2, alpha });
        graphics
          .arc(x, y, pulseRadius, angle, angle + Math.PI * 0.65)
          .stroke({ color: 0xffd387, width: 2, alpha });
      } else {
        graphics
          .circle(x, y, radius)
          .stroke({ color: 0x9cdcff, width: 2, alpha });
        for (let index = 0; index < 8; index++) {
          const angle = (index * Math.PI) / 4;
          graphics
            .moveTo(
              x + Math.cos(angle) * radius * 0.88,
              y + Math.sin(angle) * radius * 0.88,
            )
            .lineTo(x + Math.cos(angle) * radius, y + Math.sin(angle) * radius)
            .stroke({ color: 0xe8fcff, width: 2, alpha });
        }
      }
    }
  }
}
